import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from "../config/api";

// Singleton pattern to prevent multiple API calls
let subscriptionData = null;
let featuresData = [];
let isLoadingData = true;
let errorData = null;
let subscribers = new Set();
let fetchPromise = null;

// Try to restore from localStorage on module load
try {
  const savedData = localStorage.getItem('subscriptionData');
  if (savedData) {
    const parsed = JSON.parse(savedData);
    subscriptionData = parsed.subscription;
    featuresData = parsed.features;
    isLoadingData = false;
  }
} catch (e) {
  // Ignore localStorage errors
}

const notifySubscribers = () => {
  subscribers.forEach(callback => callback());
};

const fetchSubscriptionData = async () => {
  // If data is already loaded, notify subscribers immediately
  if (subscriptionData !== null && featuresData.length > 0) {
    notifySubscribers();
    return Promise.resolve();
  }

  // If already fetching, return the existing promise
  if (fetchPromise) {

    return fetchPromise;
  }

  // Start fetching
  fetchPromise = (async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        subscriptionData = null;
        featuresData = [];
        isLoadingData = false;
        errorData = null;
        notifySubscribers();
        return;
      }

      const subscriptionResponse = await axios.get(getApiUrl("/api/subscriptions/current"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      subscriptionData = subscriptionResponse.data.subscription;

      const featuresResponse = await axios.get(getApiUrl("/api/subscriptions/features"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      featuresData = featuresResponse.data;
      errorData = null;
      
      // Save to localStorage
      try {
        localStorage.setItem('subscriptionData', JSON.stringify({
          subscription: subscriptionData,
          features: featuresData
        }));
      } catch (e) {
        // Ignore localStorage errors
      }
    } catch (err) {
      errorData = err.message;

      // Check if this is an authentication error (401 Unauthorized or 403 Forbidden)
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        // Clear the invalid token
        localStorage.removeItem("token");
        // Don't redirect immediately - let the component handle it
        // This prevents interrupting other API calls that might be in progress
        return;
      }

      // Set default values on error for non-authentication issues
      subscriptionData = null;
      featuresData = [];
    } finally {

      isLoadingData = false;
      notifySubscribers();
    }
  })();

  return fetchPromise;
};

export function useSubscription() {
  const [subscription, setSubscription] = useState(subscriptionData);
  const [features, setFeatures] = useState(featuresData);
  const [isLoading, setIsLoading] = useState(isLoadingData);
  const [error, setError] = useState(errorData);

  useEffect(() => {
    const updateState = () => {
      setSubscription(subscriptionData);
      setFeatures(featuresData);
      setIsLoading(isLoadingData);
      setError(errorData);
    };

    // Subscribe to updates
    subscribers.add(updateState);

    // If data is already loaded, update immediately
    if (subscriptionData !== null || featuresData.length > 0) {
      updateState();
    } else {
      // Fetch data if not already loaded
      fetchSubscriptionData().then(() => {
        // Data will be updated via notifySubscribers
      });
    }

    // Cleanup subscription
    return () => {
      subscribers.delete(updateState);
    };
  }, []);


  const hasFeature = useCallback((featureName) => {
    const feature = features.find(f => f.name === featureName);
    return feature?.is_enabled || false;
  }, [features]);

  const hasAnyFeature = useCallback((featureNames) => {
    return featureNames.some(featureName => hasFeature(featureName));
  }, [hasFeature]);

  const hasAllFeatures = useCallback((featureNames) => {
    return featureNames.every(featureName => hasFeature(featureName));
  }, [hasFeature]);

  const getTierName = () => {
    if (!subscription) return 'No Plan';
    return subscription.tier_name || subscription.display_name || 'Unknown';
  };

  const isTrialing = () => {
    return subscription?.status === 'trialing';
  };

  const getTrialDaysRemaining = () => {
    if (!isTrialing() || !subscription?.current_period_end) return null;
    const endDate = new Date(subscription.current_period_end);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getMaxHorses = () => {
    return subscription?.max_horses || 0;
  };

  const getMaxFamilyMembers = () => {
    return subscription?.max_family_members || 0;
  };

  const isUnlimited = (limit) => {
    return limit === -1;
  };

  const canAddHorse = (currentHorseCount) => {
    const maxHorses = getMaxHorses();
    return isUnlimited(maxHorses) || currentHorseCount < maxHorses;
  };

  const canAddFamilyMember = (currentMemberCount) => {
    const maxMembers = getMaxFamilyMembers();
    return isUnlimited(maxMembers) || currentMemberCount < maxMembers;
  };

  const refreshSubscription = () => {
    fetchSubscriptionData();
  };

  return {
    subscription,
    features,
    isLoading,
    error,
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
    getTierName,
    getMaxHorses,
    getMaxFamilyMembers,
    isUnlimited,
    canAddHorse,
    canAddFamilyMember,
    refreshSubscription,
    isTrialing,
    getTrialDaysRemaining
  };
} 